import { useIntersectionObserver } from "@uidotdev/usehooks";
import { cn } from "cn";
import { useEffect, useState } from "react";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { BwCheckmarkLine } from "@/components/ui/icons";

import {
  ActiveFilterChip,
  EMPTY_VALUE_LABEL,
} from "../_components/active-filter-chip";
import { FilterValueListSkeleton } from "../_components/filter-value-list-skeleton";
import { useFilters } from "../_hooks/use-filters";
import type { FilterField, FilterOption } from "../types";

/**
 * Async, searchable single-select for large/remote option sets (e.g. cities,
 * users, places). The field's `useOptions` owns the query + debounced search +
 * infinite paging and exposes `{ options, isLoading, onSearch, hasMore,
 * fetchMore }`.
 */
export const EntityFilter = ({ field }: { field: FilterField }) => {
  const { filters, modify, clear } = useFilters();
  const value = (filters as Record<string, unknown>)[field.key] as
    | string
    | undefined;
  // entity fields always declare useOptions
  const { options, isLoading, onSearch, hasMore, fetchMore } =
    field.useOptions!();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(() => value === undefined || value === "");

  const [ref, entry] = useIntersectionObserver({
    threshold: 0.5,
    rootMargin: "100px",
  });

  useEffect(() => {
    if (entry?.isIntersecting && hasMore) fetchMore?.();
  }, [entry?.isIntersecting, hasMore, fetchMore]);

  const handleSearch = (query: string) => {
    setSearch(query);
    onSearch?.(query);
  };

  const onPick = (optionValue: string) => {
    modify({ [field.key]: optionValue === value ? undefined : optionValue });
    setOpen(false);
  };

  const selected = options.find((option) => option.value === value);
  const valueLabel = selected?.label ?? (value || EMPTY_VALUE_LABEL);

  // Selected option floats to the top (separated), mirroring the other widgets.
  const selectedOptions = options.filter((option) => option.value === value);
  const unselectedOptions = options.filter((option) => option.value !== value);

  const renderOption = (option: FilterOption) => (
    <CommandItem
      key={option.value}
      onSelect={() => onPick(option.value)}
      className="flex items-center justify-between cursor-pointer"
    >
      <span className="flex items-center gap-2 min-w-0">
        {option.icon}
        {option.detail ?? (
          <span className="line-clamp-1 break-all">{option.label}</span>
        )}
      </span>
      {value === option.value && (
        <BwCheckmarkLine className="size-4 shrink-0" />
      )}
    </CommandItem>
  );

  return (
    <ActiveFilterChip
      field={field}
      valueLabel={valueLabel}
      open={open}
      onOpenChange={setOpen}
      onRemove={() => clear(field.key)}
    >
      <Command
        className={cn(
          /* sorted */
          "h-auto max-h-[400px]",
          field.popoverWidthClassName ?? "w-[280px]",
        )}
        shouldFilter={false}
      >
        <CommandInput
          placeholder={`Search ${field.label.toLowerCase()}...`}
          className="h-9"
          value={search}
          onValueChange={handleSearch}
        />
        <CommandList>
          {isLoading && options.length === 0 ? (
            <div className="px-2 py-4">
              <FilterValueListSkeleton />
            </div>
          ) : options.length === 0 ? (
            <div className="py-6 text-center text-sm">No results found.</div>
          ) : (
            <>
              {selectedOptions.length > 0 && (
                <CommandGroup>{selectedOptions.map(renderOption)}</CommandGroup>
              )}
              {selectedOptions.length > 0 && unselectedOptions.length > 0 && (
                <CommandSeparator />
              )}
              {unselectedOptions.length > 0 && (
                <CommandGroup>
                  {unselectedOptions.map(renderOption)}
                </CommandGroup>
              )}
              {hasMore && (
                <div ref={ref} className={cn("py-2 text-center text-xs")}>
                  Loading more...
                </div>
              )}
            </>
          )}
        </CommandList>
      </Command>
    </ActiveFilterChip>
  );
};
